import * as api from 'api';
import { actionTypes } from 'redux-resource';
import { SubmissionError } from 'redux-form';

import { PER_PAGE } from 'helpers/actions';
import { expireTypeToBackendValue } from 'helpers/subs';
import { BIG_PAGE_SIZE } from '../constants';

export const createSub = (attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    requestKey: 'create',
    list: 'userSubs',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.CREATE_RESOURCES_PENDING,
  });

  return api
    .createSimoSub(attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.CREATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.CREATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const fetchAllSimoInitialsSubs =
  (params, { sort, carriers, expiry_type, status = 'expired' }) =>
  async (dispatch) => {
    const actionDefaults = {
      resourceType: 'simoSubs',
      requestKey: `fetchAllSimoInitialsSubs`,
      list: `allSimoInitialsSubs`,
      mergeListIds: false,
    };

    const apiCalls = {
      sprint: api.fetchAllInitialsSubs,
      att: api.fetchAllAttInitialsSubs,
      tracfone: api.fetchAllTracfoneInitialsSubs,
      simo: api.fetchAllSimoInitialsSubs,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_SUCCEEDED,
      resources: [],
    });

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    let subs = [];

    for (let carrier of carriers) {
      let page = 1;
      let next = 'next-url';

      while (next) {
        let response;

        try {
          response = await apiCalls[carrier](
            page,
            { ...params, [`${carrier}_status`]: status },
            BIG_PAGE_SIZE,
          );
        } catch (e) {
          let messages = e.response
            ? e.response.data
            : { detail: 'Something went wrong' };
          dispatch({
            ...actionDefaults,
            type: actionTypes.READ_RESOURCES_FAILED,
          });
          throw new SubmissionError({
            _error: messages,
          });
        }

        subs = subs.concat(response.data.results);
        next = response.data.next;
        page += 1;
      }
    }

    // sort subs if needed
    subs = typeof sort === 'function' ? sort(subs) : subs;

    // filter out subs with wrong expiry type
    subs = subs.filter(
      (sub) =>
        sub.att_status !== 'expired' ||
        sub.expiry_type === expireTypeToBackendValue(expiry_type),
    );

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_SUCCEEDED,
      resources: subs,
    });

    return subs;
  };

export const findSimoSub = (subId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    requestKey: 'find',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .findSimoSub(subId)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
        statusCode: err.response && err.response.status,
      });
    });
};

export const fetchAllSimoSubs =
  (page = 1, ordering, filters) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'simoSubs',
      requestKey: `fetchAll${page}`,
      list: `page${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchAllSimoSubs(page, ordering, filters)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const fetchAllUserSimoSubs = (userId) => async (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    requestKey: `fetchAllUserSimoSubs${userId}`,
    list: `userSimoSubs${userId}`,
    mergeListIds: false,
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  let subs = [];
  let page = 1;
  let next = 'next-url';

  while (next) {
    let response;

    try {
      response = await api.searchSimoSubs(
        page,
        { user_profile: userId },
        BIG_PAGE_SIZE,
      );
    } catch (e) {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
      let messages = e.response
        ? e.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError({
        _error: messages,
      });
    }

    subs = subs.concat(response.data.results);
    next = response.data.next;
    page += 1;
  }

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_SUCCEEDED,
    resources: subs || [],
  });

  return subs;
};

export const fetchUserFilteredSubs =
  (userId, { query, requestKey }, page = 1) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'simoSubs',
      requestKey: `${requestKey}Subs${page}`,
      list: `${requestKey}Subs${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchUserSimoFilteredSubs(userId, query, page)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const updateSimoSub = (id, sub) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    requestKey: 'updateSimoSub',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .updateSimoSub(id, sub)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const activateSub = (subId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    requestKey: 'activateSub',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .activateSimoSub(subId)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        meta: {
          activation: response.data,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail || messages.details || messages.message) {
        throw new SubmissionError({
          _error: messages,
        });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const updatePlan = (subId, plan) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    requestKey: 'updatePlan',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .updateSimoPlan(subId, plan)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail) {
        throw new SubmissionError({ _error: messages });
      } else if (
        messages.message ||
        messages.error ||
        messages.simo_subscription
      ) {
        throw new SubmissionError({
          _error:
            messages.message || messages.error || messages.simo_subscription,
        });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const fetchUpcomingPlan = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    requestKey: 'fetchUpcomingPlan',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .fetchSimoSubUpcomingPlan(id)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [id],
        meta: {
          upcomingPlan: response.data.results,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
    });
};

export const setUpcomingSimoPlan = (subId, plan) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    requestKey: 'setUpcomingSimoPlan',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .setUpcomingSimoPlan(subId, plan)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail) {
        throw new SubmissionError({ _error: messages });
      } else if (messages.message || messages.error) {
        throw new SubmissionError({
          _error: messages.message || messages.error,
        });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const toggleAutoRenew = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    requestKey: 'toggleAutoRenew',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .toggleSimoAutoRenew(id)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });

      return response;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail) {
        throw new SubmissionError({ _error: messages });
      } else if (messages.message || messages.error) {
        throw new SubmissionError({
          _error: messages.message || messages.error,
        });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const expireSimoSubscription = (subId, values) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    resources: [subId],
    requestKey: 'expireSub',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .expireSimoSubscription(subId, values)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data.subscription],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const updateSimoSubCycleType = (subId, data) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    resources: [subId],
    requestKey: 'updateSimoSubCycleType',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .updateSimoSubCycleType(subId, data)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail || messages.error) {
        throw new SubmissionError({ _error: messages });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const searchSubs =
  (page = 1, search) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'simoSubs',
      requestKey: `searchSubs${page}`, // this is used elsewhere so beware when changing
      list: `searchPage${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .searchSimoSubs(page, search)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const addSimoTopUp = (subId, values) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    resources: [subId],
    requestKey: 'addSimoTopUp',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .addSimoTopUp(subId, values)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [subId],
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (err.response.status === 402) {
        throw new SubmissionError({
          _error: { message: messages.detail, needPayment: true },
        });
      }
      if (messages.detail) {
        throw new SubmissionError({ _error: messages });
      } else if (messages.message || messages.error) {
        throw new SubmissionError({
          _error: messages.message || messages.error,
        });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const simoImmediateRenewal = (subId, plan) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    requestKey: 'simoImmediateRenewal',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .simoImmediateRenewal(subId, plan)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (err.response && err.response.status) {
        throw new SubmissionError({ messages, status: err.response.status });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const suspendSimoSub = (subId, data) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    resources: [subId],
    requestKey: 'suspendSimoSub',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .suspendSimoSubscription(subId, data.suspension_status)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail || messages.error) {
        throw new SubmissionError({ _error: messages });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const restoreSimoSub = (subId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'simoSubs',
    resources: [subId],
    requestKey: 'restoreSimoSub',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .restoreSimoSubscription(subId)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail || messages.error) {
        throw new SubmissionError({ _error: messages });
      } else {
        throw new SubmissionError(messages);
      }
    });
};
